import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Play, Download, RefreshCw, AlertTriangle, ImageOff, Loader2, ShieldAlert, Award, Ban, FileSignature, CheckCircle2 } from "lucide-react";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

/**
 * Phase 6 Item A — Model Selection Viewer.
 *
 * Spec ref: /app/memory/PHASE_6_RESEARCH_BRIEF.md § A.
 *
 * Admin-only. Surfaces the test battery × model grid for scoring.
 * Two views:
 *   - Per-model (default): one model at a time, each case in its own card.
 *   - Side-by-side comparison grid: rows = cases, cols = models.
 *     Load-bearing for hard cases (D-7 crowds, C-5 Naira-on-desk) where
 *     one model defaults to banned photorealism and another doesn't —
 *     side-by-side is the fastest path to seeing the failure.
 *
 * Scoring: 1-5 (1=unusable, 5=ship-ready) + free-text notes + policy_violation
 * checkbox. Persists to /api/phase6/runs/{run_id}/score (upsert keyed by scorer).
 */
export default function Phase6ModelComparison({ user }) {
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [run, setRun] = useState(null);
  const [results, setResults] = useState([]);
  const [scores, setScores] = useState([]);
  const [scorecard, setScorecard] = useState(null);
  const [battery, setBattery] = useState([]);
  const [models, setModels] = useState([]);
  const [viewMode, setViewMode] = useState("compare"); // "compare" | "per_model" | "scorecard"
  const [selectedModel, setSelectedModel] = useState(null);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingRun, setLoadingRun] = useState(false);
  const [starting, setStarting] = useState(false);
  const [pollingActive, setPollingActive] = useState(false);
  const [budget, setBudget] = useState(null);
  const [d7rerunStarting, setD7rerunStarting] = useState(false);
  const [activeDecision, setActiveDecision] = useState(null);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [quickModel, setQuickModel] = useState("nano_banana_flash");
  const [quickActivating, setQuickActivating] = useState(false);

  const isAdmin = user?.role === "admin";

  // ─── Data loaders ─────────────────────────────────────────────────
  const loadBudget = useCallback(async () => {
    try {
      const r = await fetch(`${API}/phase6/budget-status`, { credentials: "include" });
      if (r.ok) setBudget(await r.json());
    } catch (e) {
      console.error("loadBudget failed", e);
    }
  }, []);

  const loadActiveDecision = useCallback(async () => {
    try {
      const r = await fetch(`${API}/phase6/decisions/active`, { credentials: "include" });
      if (r.ok) {
        const j = await r.json();
        setActiveDecision(j.decision || null);
      }
    } catch (e) {
      console.error("loadActiveDecision failed", e);
    }
  }, []);
  const quickActivate = useCallback(async () => {
    setQuickActivating(true);
    try {
      const r = await fetch(`${API}/phase6/decisions/quick-activate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ primary_model: quickModel }),
      });
      const j = await r.json();
      if (!r.ok) { toast.error(j?.detail || "Could not activate model"); return; }
      setActiveDecision(j.decision || null);
      toast.success("Model activated — cover and image generation is now unblocked.");
    } catch (e) {
      toast.error("Activation failed");
    } finally {
      setQuickActivating(false);
    }
  }, [quickModel]);

  const loadRefData = useCallback(async () => {
    try {
      const [bRes, mRes] = await Promise.all([
        fetch(`${API}/phase6/battery`, { credentials: "include" }),
        fetch(`${API}/phase6/models`, { credentials: "include" }),
      ]);
      if (bRes.ok) {
        const b = await bRes.json();
        setBattery(b.cases || []);
      }
      if (mRes.ok) {
        const m = await mRes.json();
        setModels(m.models || []);
        if (m.models?.length && !selectedModel) setSelectedModel(m.models[0].id);
      }
    } catch (e) {
      console.error("phase6 ref load failed", e);
    }
  }, [selectedModel]);

  const loadRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const r = await fetch(`${API}/phase6/runs`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setRuns(j.runs || []);
      if (!selectedRunId && j.runs?.length) setSelectedRunId(j.runs[0].run_id);
    } catch (e) {
      console.error("loadRuns failed", e);
      toast.error("Failed to load runs");
    } finally {
      setLoadingRuns(false);
    }
  }, [selectedRunId]);

  const loadRun = useCallback(async (runId) => {
    if (!runId) return;
    setLoadingRun(true);
    try {
      const [rRes, scRes] = await Promise.all([
        fetch(`${API}/phase6/runs/${runId}`, { credentials: "include" }),
        fetch(`${API}/phase6/runs/${runId}/scorecard`, { credentials: "include" }),
      ]);
      if (!rRes.ok) throw new Error(`HTTP ${rRes.status}`);
      const j = await rRes.json();
      setRun(j.run || null);
      setResults(j.results || []);
      setScores(j.scores || []);
      if (scRes.ok) {
        const sc = await scRes.json();
        setScorecard(sc);
      } else {
        setScorecard(null);
      }
    } catch (e) {
      console.error("loadRun failed", e);
      toast.error("Failed to load run detail");
    } finally {
      setLoadingRun(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) loadRefData(); }, [isAdmin, loadRefData]);
  useEffect(() => { if (isAdmin) loadRuns(); }, [isAdmin, loadRuns]);
  useEffect(() => { if (isAdmin) loadBudget(); }, [isAdmin, loadBudget]);
  useEffect(() => { if (isAdmin) loadActiveDecision(); }, [isAdmin, loadActiveDecision]);
  useEffect(() => { if (selectedRunId) loadRun(selectedRunId); }, [selectedRunId, loadRun]);

  // Auto-poll while the selected run is still running
  useEffect(() => {
    if (!run || run.status !== "running") { setPollingActive(false); return; }
    setPollingActive(true);
    const interval = setInterval(() => loadRun(run.run_id), 5000);
    return () => clearInterval(interval);
  }, [run, loadRun]);

  // ─── Actions ──────────────────────────────────────────────────────
  const startRun = async () => {
    setStarting(true);
    try {
      const r = await fetch(`${API}/phase6/runs`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes: `Run started ${new Date().toISOString()}` }),
      });
      if (r.status === 429) {
        const j = await r.json();
        toast.error(`Image-gen budget ceiling reached: ${j.detail?.message || "monthly cap hit"}`);
        await loadBudget();
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      toast.success(`Run started: ${j.total_pairs} images, ~$${j.estimated_cost_usd}`);
      setSelectedRunId(j.run_id);
      await loadRuns();
      await loadBudget();
    } catch (e) {
      console.error("startRun failed", e);
      toast.error("Failed to start run");
    } finally {
      setStarting(false);
    }
  };

  const startD7ConstrainedRerun = async () => {
    setD7rerunStarting(true);
    try {
      const r = await fetch(`${API}/phase6/d7-constrained-rerun`, {
        method: "POST",
        credentials: "include",
      });
      if (r.status === 429) {
        const j = await r.json();
        toast.error(`Budget ceiling reached: ${j.detail?.error || "monthly cap hit"}`);
        await loadBudget();
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      toast.success(`D-7 constrained re-run started: 3 images, ~$${j.estimated_cost_usd}`);
      setSelectedRunId(j.run_id);
      await loadRuns();
      await loadBudget();
    } catch (e) {
      console.error("startD7ConstrainedRerun failed", e);
      toast.error("Failed to start D-7 constrained re-run");
    } finally {
      setD7rerunStarting(false);
    }
  };

  const submitScore = async ({ case_id, model, score, notes, policy_violation }) => {
    if (!run) return;
    try {
      const r = await fetch(`${API}/phase6/runs/${run.run_id}/score`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ case_id, model, score, notes, policy_violation }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Optimistic local merge
      setScores((prev) => {
        const filtered = prev.filter(
          (s) => !(s.case_id === case_id && s.model === model && s.scored_by === user?.email)
        );
        return [
          ...filtered,
          { case_id, model, score, notes, policy_violation, scored_by: user?.email, run_id: run.run_id },
        ];
      });
    } catch (e) {
      console.error("submitScore failed", e);
      toast.error("Score save failed");
    }
  };

  const exportCsv = () => {
    if (!run) return;
    window.open(`${API}/phase6/runs/${run.run_id}/export.csv`, "_blank");
  };

  const submitDecision = async (payload) => {
    setSigning(true);
    try {
      const r = await fetch(`${API}/phase6/decisions/sign`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(`Sign failed: ${j.detail || r.status}`);
        return false;
      }
      const j = await r.json();
      toast.success(`Decision signed (v${j.decision.version}) by ${j.decision.signed_by}`);
      await loadActiveDecision();
      setSignModalOpen(false);
      return true;
    } catch (e) {
      console.error("submitDecision failed", e);
      toast.error("Sign failed (network)");
      return false;
    } finally {
      setSigning(false);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────
  const myScore = useCallback((case_id, model) => {
    return scores.find(
      (s) => s.case_id === case_id && s.model === model && s.scored_by === user?.email
    );
  }, [scores, user?.email]);

  const resultFor = useCallback((case_id, model) => {
    return results.find((r) => r.case_id === case_id && r.model === model);
  }, [results]);

  // For runs that contain synthetic case_ids (e.g. the D-7 constrained re-run's
  // 'D-7-CONSTRAINED' synthetic case), augment the locked battery so the grid
  // actually renders cells for them. Synthetic cases inherit category/difficulty
  // from the first result row that referenced them.
  const effectiveBattery = (() => {
    const batteryIds = new Set(battery.map((b) => b.case_id));
    const extraCases = [];
    const seen = new Set();
    for (const r of results) {
      if (batteryIds.has(r.case_id) || seen.has(r.case_id)) continue;
      seen.add(r.case_id);
      extraCases.push({
        case_id: r.case_id,
        category: r.case_category || "?",
        difficulty: r.case_difficulty || "hard",
        title: r.case_title || r.case_id,
        prompt: r.prompt_sent || "",
        intent: "Synthetic case from an ad-hoc re-run (not in the locked battery).",
        failure_mode: "Inherits from parent case — score relative to the original.",
      });
    }
    // Synthetic cases first (they're the focus of an ad-hoc re-run).
    return [...extraCases, ...battery];
  })();

  const imageUrl = (run_id, model, case_id) =>
    `${API}/phase6/image/${run_id}/${model}/${case_id}`;

  // ─── Auth guard ───────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="phase6-admin-gate">
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 flex gap-2">
          <ShieldAlert className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Admin access required</p>
            <p className="text-sm">
              The Phase 6 model-comparison view surfaces raw model outputs including
              policy-edge and failed generations. Internal evaluation material — gated
              like Calibration and Mission Control.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6" data-testid="phase6-model-comparison">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Phase 6 — Model Selection</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Item A test battery × Emergent-key image models. Score 1-5 and flag policy
            violations. Side-by-side grid is the fast path for hard-case discrimination.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {budget && (
            <div
              className={`rounded-md border px-3 py-1.5 text-xs ${
                budget.allowed
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-red-300 bg-red-50 text-red-900"
              }`}
              data-testid="phase6-budget-pill"
              title="Image-gen monthly ceiling enforced independently of the Emergent global budget. Resets at the start of each calendar month."
            >
              <span className="font-semibold">Image-gen budget</span>
              <span className="ml-2">
                ${budget.month_to_date_usd.toFixed(2)} / ${budget.ceiling_usd.toFixed(0)}
              </span>
              <span className="ml-2 text-slate-500">({budget.month_key})</span>
            </div>
          )}
          <Button
            onClick={startRun}
            disabled={starting}
            data-testid="phase6-start-run-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {starting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
            New full run ({battery.length}×{models.length})
          </Button>
          <Button
            onClick={startD7ConstrainedRerun}
            disabled={d7rerunStarting}
            data-testid="phase6-d7-constrained-rerun-btn"
            variant="outline"
            className="border-amber-300 text-amber-900 hover:bg-amber-50"
            title="Re-run D-7 across all 3 models with a constrained, policy-explicit prompt. Tests whether D-7's policy failure is a prompt problem or a model problem."
          >
            {d7rerunStarting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
            D-7 constrained re-run (~$0.12)
          </Button>
          <Button
            variant="outline"
            onClick={() => { loadRuns(); loadBudget(); if (selectedRunId) loadRun(selectedRunId); }}
            disabled={loadingRuns || loadingRun}
            data-testid="phase6-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${(loadingRuns || loadingRun) ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Run picker + meta */}
      <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="phase6-run-select" className="text-sm font-medium text-slate-700">Run:</label>
          <select
            id="phase6-run-select"
            value={selectedRunId || ""}
            onChange={(e) => setSelectedRunId(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1 text-sm min-w-[300px]"
            data-testid="phase6-run-select"
          >
            <option value="">— select a run —</option>
            {runs.map((r) => (
              <option key={r.run_id} value={r.run_id}>
                {r.run_id} · {r.status} · {r.total_pairs} pairs · ${(r.total_cost_usd_estimate ?? 0).toFixed(2)}
              </option>
            ))}
          </select>
          {run && (
            <>
              <Badge variant={run.status === "completed" ? "default" : run.status === "running" ? "secondary" : "destructive"}
                     data-testid="phase6-run-status">
                {run.status}
              </Badge>
              <span className="text-xs text-slate-600">
                Started by <span className="font-mono">{run.started_by}</span> at{" "}
                {new Date(run.started_at).toLocaleString()}
              </span>
              {run.error_count > 0 && (
                <Badge variant="destructive" data-testid="phase6-run-errors">
                  <AlertTriangle className="h-3 w-3 mr-1" /> {run.error_count} errors
                </Badge>
              )}
              {pollingActive && (
                <span className="text-xs text-emerald-700 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> polling…
                </span>
              )}
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="phase6-export-csv-btn">
                <Download className="h-3 w-3 mr-1" /> CSV
              </Button>
            </>
          )}
        </div>

        {/* View mode toggle */}
        {run && (
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <span className="text-xs font-medium text-slate-600">View:</span>
            <Button
              size="sm"
              variant={viewMode === "compare" ? "default" : "outline"}
              onClick={() => setViewMode("compare")}
              data-testid="phase6-view-compare-btn"
            >
              Side-by-side grid
            </Button>
            <Button
              size="sm"
              variant={viewMode === "per_model" ? "default" : "outline"}
              onClick={() => setViewMode("per_model")}
              data-testid="phase6-view-per-model-btn"
            >
              Per model
            </Button>
            <Button
              size="sm"
              variant={viewMode === "scorecard" ? "default" : "outline"}
              onClick={() => setViewMode("scorecard")}
              data-testid="phase6-view-scorecard-btn"
            >
              <Award className="h-3 w-3 mr-1" /> Scorecard
            </Button>
            {viewMode === "per_model" && (
              <select
                value={selectedModel || ""}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1 text-sm ml-2"
                data-testid="phase6-per-model-select"
              >
                {models.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      {isAdmin && !activeDecision && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 mb-4" data-testid="quick-activate-banner">
          <div className="text-sm font-semibold text-amber-900">No active model — generation is blocked</div>
          <p className="text-xs text-amber-800 mt-1 max-w-2xl">
            Covers and AI images need an active model. You don't have to run the comparison below —
            just pick a default model and activate it. You can always run a full comparison later.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <select
              data-testid="quick-activate-model"
              value={quickModel}
              onChange={(e) => setQuickModel(e.target.value)}
              className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm"
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={quickActivate}
              disabled={quickActivating || !models.length}
              data-testid="quick-activate-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {quickActivating ? "Activating…" : "Use this model"}
            </Button>
          </div>
        </div>
      )}

      {!run && (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-600">
          No run selected. Start a new full run above, or pick one from the dropdown.
        </div>
      )}

      {run && viewMode === "compare" && (
        <CompareGrid
          battery={effectiveBattery}
          models={models}
          resultFor={resultFor}
          myScore={myScore}
          submitScore={submitScore}
          imageUrl={imageUrl}
          runId={run.run_id}
        />
      )}

      {run && viewMode === "per_model" && selectedModel && (
        <PerModelView
          battery={effectiveBattery}
          model={models.find((m) => m.id === selectedModel)}
          resultFor={resultFor}
          myScore={myScore}
          submitScore={submitScore}
          imageUrl={imageUrl}
          runId={run.run_id}
        />
      )}

      {run && viewMode === "scorecard" && (
        <ScorecardView
          scorecard={scorecard}
          models={models}
          runs={runs}
          run={run}
          activeDecision={activeDecision}
          onOpenSignModal={() => setSignModalOpen(true)}
          user={user}
        />
      )}

      {signModalOpen && (
        <SignDecisionModal
          scorecard={scorecard}
          models={models}
          runs={runs}
          currentRunId={run?.run_id}
          activeDecision={activeDecision}
          user={user}
          signing={signing}
          onClose={() => setSignModalOpen(false)}
          onSubmit={submitDecision}
        />
      )}
    </div>
  );
}


// ─── Side-by-side grid ─────────────────────────────────────────────────
function CompareGrid({ battery, models, resultFor, myScore, submitScore, imageUrl, runId }) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-200 bg-white" data-testid="phase6-compare-grid">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left p-3 font-semibold text-slate-700 w-[280px] sticky left-0 bg-slate-50 z-10">
              Test case
            </th>
            {models.map((m) => (
              <th key={m.id} className="text-left p-3 font-semibold text-slate-700 min-w-[320px]">
                {m.label}
                <div className="text-[10px] font-mono text-slate-500 font-normal">{m.model_string}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {battery.map((c) => (
            <tr key={c.case_id} className="border-b border-slate-100">
              <td className="p-3 align-top sticky left-0 bg-white z-10 border-r border-slate-200">
                <CaseHeader case_={c} />
              </td>
              {models.map((m) => (
                <td key={m.id} className="p-3 align-top border-r border-slate-100" data-testid={`phase6-cell-${c.case_id}-${m.id}`}>
                  <Cell
                    case_={c}
                    model={m}
                    result={resultFor(c.case_id, m.id)}
                    score={myScore(c.case_id, m.id)}
                    onScore={(payload) => submitScore({ case_id: c.case_id, model: m.id, ...payload })}
                    imageUrl={imageUrl}
                    runId={runId}
                    compact
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function PerModelView({ battery, model, resultFor, myScore, submitScore, imageUrl, runId }) {
  if (!model) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="phase6-per-model-grid">
      {battery.map((c) => (
        <div key={c.case_id} className="rounded-md border border-slate-200 bg-white p-3 space-y-2"
             data-testid={`phase6-pm-cell-${c.case_id}`}>
          <CaseHeader case_={c} />
          <Cell
            case_={c}
            model={model}
            result={resultFor(c.case_id, model.id)}
            score={myScore(c.case_id, model.id)}
            onScore={(payload) => submitScore({ case_id: c.case_id, model: model.id, ...payload })}
            imageUrl={imageUrl}
            runId={runId}
          />
        </div>
      ))}
    </div>
  );
}


function CaseHeader({ case_ }) {
  const tone = case_.difficulty === "hard"
    ? "bg-amber-100 text-amber-900 border-amber-300"
    : "bg-emerald-100 text-emerald-900 border-emerald-300";
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs font-bold text-slate-700">{case_.case_id}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tone}`}>
          {case_.difficulty === "hard" ? "hard" : "happy"} · Cat {case_.category}
        </span>
      </div>
      <div className="font-semibold text-slate-900 text-sm leading-tight">{case_.title}</div>
      <div className="text-[11px] text-slate-600 leading-snug" title={case_.prompt}>
        <span className="font-medium">Prompt:</span> {case_.prompt}
      </div>
      {case_.difficulty === "hard" && (
        <div className="text-[11px] text-amber-800 leading-snug">
          <span className="font-medium">Failure mode:</span> {case_.failure_mode}
        </div>
      )}
    </div>
  );
}


function Cell({ case_, model, result, score, onScore, imageUrl, runId, compact = false }) {
  const [notes, setNotes] = useState(score?.notes || "");
  const [policyViolation, setPolicyViolation] = useState(!!score?.policy_violation);
  const [savingScore, setSavingScore] = useState(null); // the score being saved

  useEffect(() => {
    setNotes(score?.notes || "");
    setPolicyViolation(!!score?.policy_violation);
  }, [score?.notes, score?.policy_violation, runId, case_.case_id, model.id]);

  const handleScore = async (n) => {
    setSavingScore(n);
    await onScore({ score: n, notes, policy_violation: policyViolation });
    setSavingScore(null);
  };

  const handleNotesBlur = async () => {
    if (!score) return; // need a score first
    if (notes === score.notes && policyViolation === !!score.policy_violation) return;
    await onScore({ score: score.score, notes, policy_violation: policyViolation });
  };

  const handlePolicyToggle = async () => {
    const next = !policyViolation;
    setPolicyViolation(next);
    if (score) {
      await onScore({ score: score.score, notes, policy_violation: next });
    }
  };

  if (!result) {
    return (
      <div className="text-center text-xs text-slate-400 italic py-6" data-testid={`phase6-pending-${case_.case_id}-${model.id}`}>
        pending generation…
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Image */}
      <div className="relative rounded border border-slate-200 bg-slate-50 overflow-hidden">
        {result.has_image ? (
          <img
            src={imageUrl(runId, model.id, case_.case_id)}
            alt={`${case_.case_id} / ${model.label}`}
            className={`${compact ? "w-full h-44" : "w-full h-56"} object-cover cursor-zoom-in`}
            data-testid={`phase6-img-${case_.case_id}-${model.id}`}
            onClick={() => window.open(imageUrl(runId, model.id, case_.case_id), "_blank", "noopener,noreferrer")}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-44 text-xs text-slate-500" data-testid={`phase6-err-${case_.case_id}-${model.id}`}>
            <ImageOff className="h-6 w-6 mb-1" />
            <span className="text-red-600 px-2 text-center">{result.error || "no image"}</span>
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
        <span>latency: {result.latency_ms}ms</span>
        <span>·</span>
        <span>cost≈${result.cost_usd_estimate?.toFixed(3)}</span>
        {policyViolation && (
          <span className="ml-auto inline-flex items-center gap-1 text-red-700 font-medium" data-testid={`phase6-policy-flag-${case_.case_id}-${model.id}`}>
            <AlertTriangle className="h-3 w-3" /> policy violation
          </span>
        )}
      </div>

      {/* Score buttons */}
      <div className="flex items-center gap-1" data-testid={`phase6-score-row-${case_.case_id}-${model.id}`}>
        <span className="text-[10px] text-slate-500 mr-1">Score:</span>
        {[1, 2, 3, 4, 5].map((n) => {
          const selected = score?.score === n;
          return (
            <button
              key={n}
              onClick={() => handleScore(n)}
              disabled={savingScore !== null}
              className={`w-7 h-7 rounded text-xs font-bold border transition-colors ${
                selected
                  ? "bg-blue-600 text-white border-blue-700"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
              } ${savingScore === n ? "opacity-50" : ""}`}
              data-testid={`phase6-score-${case_.case_id}-${model.id}-${n}`}
              aria-label={`Score ${n}`}
            >
              {savingScore === n ? "…" : n}
            </button>
          );
        })}
      </div>

      {/* Policy violation toggle */}
      <label className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={policyViolation}
          onChange={handlePolicyToggle}
          data-testid={`phase6-policy-toggle-${case_.case_id}-${model.id}`}
          className="h-3 w-3"
        />
        Policy violation (depictive / photorealistic)
      </label>

      {/* Notes */}
      <Textarea
        placeholder="Notes (specific failure mode, etc.)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={handleNotesBlur}
        className="text-xs min-h-[48px]"
        data-testid={`phase6-notes-${case_.case_id}-${model.id}`}
      />
    </div>
  );
}



// ─── Scorecard view ────────────────────────────────────────────────────
function ScorecardView({ scorecard, models, runs, run, activeDecision, onOpenSignModal, user }) {
  if (!scorecard) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
        Loading scorecard…
      </div>
    );
  }

  const { recommendation, by_model_overall, by_model_category, discipline_note, totals } = scorecard;
  const modelLabel = (id) => models.find((m) => m.id === id)?.label || id;
  const canSign = user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="phase6-scorecard-view">
      {/* Active decision banner */}
      {activeDecision ? (
        <div className="rounded-md border-2 border-emerald-400 bg-emerald-50 p-4 space-y-2"
             data-testid="phase6-active-decision-banner">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-700" />
              <span className="text-xs font-semibold uppercase tracking-wide text-emerald-900">
                Active signed decision · v{activeDecision.version}
              </span>
            </div>
            <button
              onClick={onOpenSignModal}
              className="text-xs text-emerald-800 underline hover:text-emerald-900"
              data-testid="phase6-supersede-decision-btn"
            >
              Supersede with new signature
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            <div>
              <span className="text-xs text-emerald-700">Primary:</span>{" "}
              <span className="font-semibold text-emerald-900" data-testid="phase6-active-primary">
                {modelLabel(activeDecision.primary_model)}
              </span>
            </div>
            <div>
              <span className="text-xs text-emerald-700">Fallback:</span>{" "}
              <span className="font-semibold text-emerald-900" data-testid="phase6-active-fallback">
                {activeDecision.fallback_model ? modelLabel(activeDecision.fallback_model) : "—"}
              </span>
            </div>
            <div>
              <span className="text-xs text-emerald-700">Skipped:</span>{" "}
              <span className="text-emerald-800">
                {activeDecision.skipped_models?.length ? activeDecision.skipped_models.map(modelLabel).join(", ") : "—"}
              </span>
            </div>
          </div>
          <div className="text-[11px] text-emerald-700">
            Signed by <span className="font-mono">{activeDecision.signed_by}</span> at{" "}
            {new Date(activeDecision.signed_at).toLocaleString()} ·{" "}
            evidence runs: <span className="font-mono">{activeDecision.evidence_run_ids?.join(", ")}</span>
          </div>
        </div>
      ) : (
        canSign && (
          <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-4 flex items-start justify-between gap-3"
               data-testid="phase6-no-active-decision-banner">
            <div className="flex items-start gap-2">
              <FileSignature className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-amber-900">No active signed decision</div>
                <div className="text-xs text-amber-800 mt-0.5">
                  The Phase 6 build reads its model preference from the active signed decision row.
                  Sign this verdict to bind primary + fallback for production.
                </div>
              </div>
            </div>
            <button
              onClick={onOpenSignModal}
              className="bg-amber-700 hover:bg-amber-800 text-white text-sm font-medium px-3 py-1.5 rounded flex items-center gap-1 flex-shrink-0"
              data-testid="phase6-sign-verdict-btn"
            >
              <FileSignature className="h-4 w-4" /> Sign verdict
            </button>
          </div>
        )
      )}

      {/* Discipline banner */}
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex gap-2"
           data-testid="phase6-discipline-note">
        <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold mb-1">Disqualification discipline</p>
          <p>{discipline_note}</p>
        </div>
      </div>

      {/* Recommendation banner */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="phase6-recommendation-banner">
        {/* Primary */}
        <div className={`rounded-md border-2 p-4 ${
          recommendation.primary.model
            ? "border-emerald-400 bg-emerald-50"
            : "border-slate-300 bg-slate-50"
        }`} data-testid="phase6-rec-primary">
          <div className="flex items-center gap-2 mb-2">
            <Award className={recommendation.primary.model ? "h-5 w-5 text-emerald-700" : "h-5 w-5 text-slate-400"} />
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Recommended primary
            </span>
          </div>
          <div className="text-lg font-bold text-slate-900" data-testid="phase6-rec-primary-model">
            {recommendation.primary.model ? modelLabel(recommendation.primary.model) : "— none —"}
          </div>
          <p className="text-xs text-slate-700 mt-1">{recommendation.primary.rationale}</p>
        </div>

        {/* Fallback */}
        <div className={`rounded-md border p-4 ${
          recommendation.fallback.model
            ? "border-blue-300 bg-blue-50"
            : "border-slate-200 bg-slate-50"
        }`} data-testid="phase6-rec-fallback">
          <div className="flex items-center gap-2 mb-2">
            <Award className={recommendation.fallback.model ? "h-5 w-5 text-blue-600" : "h-5 w-5 text-slate-400"} />
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Recommended fallback
            </span>
          </div>
          <div className="text-lg font-bold text-slate-900" data-testid="phase6-rec-fallback-model">
            {recommendation.fallback.model ? modelLabel(recommendation.fallback.model) : "— none —"}
          </div>
          <p className="text-xs text-slate-700 mt-1">{recommendation.fallback.rationale}</p>
        </div>
      </div>

      {/* Disqualified models */}
      {recommendation.disqualified.length > 0 && (
        <div className="rounded-md border-2 border-red-300 bg-red-50 p-4 space-y-2"
             data-testid="phase6-disqualified-section">
          <div className="flex items-center gap-2 mb-1">
            <Ban className="h-5 w-5 text-red-700" />
            <span className="text-sm font-semibold text-red-900">Disqualified — policy failure on a hard case</span>
          </div>
          <p className="text-xs text-red-800">
            Disqualification is a hard gate, regardless of quality score. Quality
            averages shown for context only.
          </p>
          <div className="space-y-2 pt-1">
            {recommendation.disqualified.map((d) => (
              <div key={d.model} className="rounded border border-red-200 bg-white p-2 text-xs"
                   data-testid={`phase6-disqualified-${d.model}`}>
                <div className="font-semibold text-red-900">{modelLabel(d.model)}</div>
                <div className="text-slate-700 mt-0.5">{d.summary}</div>
                <div className="text-[10px] text-slate-500 mt-1">First reason: {d.top_reason}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-model overall table */}
      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">
          Per-model overall ({totals.n_scores} scored cells of {totals.n_results} generated)
        </div>
        <table className="min-w-full text-sm" data-testid="phase6-overall-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs">
            <tr>
              <th className="text-left p-2">Model</th>
              <th className="text-right p-2">Cells scored</th>
              <th className="text-right p-2">Overall avg</th>
              <th className="text-right p-2">Policy violations</th>
              <th className="text-left p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {by_model_overall.map((m) => (
              <tr key={m.model} className="border-b border-slate-100" data-testid={`phase6-overall-row-${m.model}`}>
                <td className="p-2 font-medium text-slate-900">{modelLabel(m.model)}</td>
                <td className="p-2 text-right text-slate-700">{m.n_scored}</td>
                <td className="p-2 text-right font-mono">
                  {m.overall_avg !== null ? m.overall_avg.toFixed(2) : "—"}
                </td>
                <td className="p-2 text-right">
                  {m.total_policy_violations > 0 ? (
                    <span className="text-red-700 font-semibold">{m.total_policy_violations}</span>
                  ) : (
                    <span className="text-slate-400">0</span>
                  )}
                </td>
                <td className="p-2">
                  {m.is_recommendable ? (
                    <span className="text-emerald-700 text-xs font-medium">recommendable</span>
                  ) : (
                    <span className="text-red-700 text-xs font-medium" data-testid={`phase6-status-dq-${m.model}`}>
                      disqualified · Cat {m.categories_disqualified.join(", ")}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per-model × category breakdown */}
      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">
          By model × category
        </div>
        <table className="min-w-full text-sm" data-testid="phase6-category-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs">
            <tr>
              <th className="text-left p-2">Model</th>
              <th className="text-left p-2">Category</th>
              <th className="text-right p-2">Cells scored</th>
              <th className="text-right p-2">Happy avg</th>
              <th className="text-right p-2">Hard avg</th>
              <th className="text-right p-2">Hard PV</th>
              <th className="text-left p-2">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {by_model_category.map((s) => (
              <tr key={`${s.model}-${s.category}`}
                  className={`border-b border-slate-100 ${s.disqualified ? "bg-red-50" : ""}`}
                  data-testid={`phase6-mc-row-${s.model}-${s.category}`}>
                <td className="p-2 font-medium text-slate-900">{modelLabel(s.model)}</td>
                <td className="p-2">Category {s.category}</td>
                <td className="p-2 text-right">{s.n_scored}</td>
                <td className="p-2 text-right font-mono">
                  {s.happy_avg !== null ? s.happy_avg.toFixed(2) : "—"}
                </td>
                <td className="p-2 text-right font-mono">
                  {s.hard_avg !== null ? s.hard_avg.toFixed(2) : "—"}
                </td>
                <td className="p-2 text-right">
                  {s.policy_violations_hard > 0 ? (
                    <span className="text-red-700 font-semibold">{s.policy_violations_hard}</span>
                  ) : (
                    <span className="text-slate-400">0</span>
                  )}
                </td>
                <td className="p-2">
                  {s.disqualified ? (
                    <span className="text-red-700 text-xs font-medium">disqualified</span>
                  ) : s.n_scored === 0 ? (
                    <span className="text-slate-400 text-xs">no scores</span>
                  ) : (
                    <span className="text-emerald-700 text-xs">clean</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── Sign-decision modal ───────────────────────────────────────────────
function SignDecisionModal({ scorecard, models, runs, currentRunId, activeDecision, user, signing, onClose, onSubmit }) {
  const allModelIds = models.map((m) => m.id);

  const defaultPrimary =
    scorecard?.recommendation?.primary?.model ||
    activeDecision?.primary_model ||
    "";
  const defaultFallback =
    scorecard?.recommendation?.fallback?.model ||
    activeDecision?.fallback_model ||
    "";

  const [primary, setPrimary] = useState(defaultPrimary);
  const [fallback, setFallback] = useState(defaultFallback);
  const [evidenceRunIds, setEvidenceRunIds] = useState(currentRunId ? [currentRunId] : []);
  const [rationale, setRationale] = useState(
    activeDecision?.rationale || scorecard?.recommendation?.primary?.rationale || ""
  );
  const [notes, setNotes] = useState("");

  const skippedModels = allModelIds.filter((id) => id !== primary && id !== fallback);

  const toggleEvidenceRun = (runId) => {
    setEvidenceRunIds((prev) =>
      prev.includes(runId) ? prev.filter((r) => r !== runId) : [...prev, runId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!primary) { toast.error("Primary model required"); return; }
    if (evidenceRunIds.length === 0) { toast.error("At least one evidence run required"); return; }
    if (rationale.trim().length < 20) { toast.error("Rationale needs at least 20 characters"); return; }

    const cat_c_scores = {};
    const cat_d_scores = {};
    if (scorecard?.by_model_category) {
      for (const s of scorecard.by_model_category) {
        if (s.avg_score !== null) {
          if (s.category === "C") cat_c_scores[s.model] = s.avg_score;
          if (s.category === "D") cat_d_scores[s.model] = s.avg_score;
        }
      }
    }

    await onSubmit({
      primary_model: primary,
      fallback_model: fallback || null,
      skipped_models: skippedModels,
      evidence_run_ids: evidenceRunIds,
      rationale,
      cat_c_scores,
      cat_d_scores,
      notes,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="phase6-sign-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Sign model-selection decision</h2>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">×</button>
          </div>

          <div className="p-5 space-y-4">
            <div className="text-xs text-slate-600 bg-slate-50 rounded p-3 leading-relaxed">
              This will sign a new active decision as <span className="font-mono font-semibold">{user?.email}</span>.
              {activeDecision && (
                <> The current active decision (v{activeDecision.version}, signed by{" "}
                  <span className="font-mono">{activeDecision.signed_by}</span>) will be superseded.</>
              )}
            </div>

            <div>
              <label htmlFor="sign-primary" className="block text-xs font-medium text-slate-700 mb-1">Primary model</label>
              <select id="sign-primary" value={primary} onChange={(e) => setPrimary(e.target.value)}
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                      data-testid="phase6-sign-primary" required>
                <option value="">— select —</option>
                {models.map((m) => (<option key={m.id} value={m.id}>{m.label}</option>))}
              </select>
            </div>

            <div>
              <label htmlFor="sign-fallback" className="block text-xs font-medium text-slate-700 mb-1">Fallback model (optional)</label>
              <select id="sign-fallback" value={fallback} onChange={(e) => setFallback(e.target.value)}
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                      data-testid="phase6-sign-fallback">
                <option value="">— none —</option>
                {models.filter((m) => m.id !== primary).map((m) => (<option key={m.id} value={m.id}>{m.label}</option>))}
              </select>
            </div>

            <div>
              <div className="block text-xs font-medium text-slate-700 mb-1">Skipped (auto-computed)</div>
              <div className="text-sm text-slate-600 bg-slate-50 rounded px-2 py-1.5">
                {skippedModels.length === 0 ? "—" : skippedModels.map((id) => models.find((m) => m.id === id)?.label || id).join(", ")}
              </div>
            </div>

            <div>
              <div className="block text-xs font-medium text-slate-700 mb-1">Evidence runs (at least one)</div>
              <div className="border border-slate-200 rounded p-2 max-h-40 overflow-y-auto space-y-1">
                {runs.length === 0 && <div className="text-xs text-slate-400">No runs available.</div>}
                {runs.map((r) => (
                  <label key={r.run_id} className="flex items-center gap-2 text-xs hover:bg-slate-50 rounded px-1 py-0.5 cursor-pointer">
                    <input type="checkbox" checked={evidenceRunIds.includes(r.run_id)}
                           onChange={() => toggleEvidenceRun(r.run_id)}
                           data-testid={`phase6-sign-evidence-${r.run_id}`} />
                    <span className="font-mono text-slate-700">{r.run_id}</span>
                    <span className="text-slate-500">· {r.status}</span>
                    <span className="text-slate-500">· {r.total_pairs} pairs</span>
                    <span className="text-slate-500">· ${(r.total_cost_usd_estimate ?? 0).toFixed(2)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="sign-rationale" className="block text-xs font-medium text-slate-700 mb-1">
                Rationale (min 20 chars)
              </label>
              <textarea id="sign-rationale" value={rationale} onChange={(e) => setRationale(e.target.value)}
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm min-h-[100px]"
                        data-testid="phase6-sign-rationale" required minLength={20} />
              <div className="text-[11px] text-slate-500 mt-0.5">{rationale.length} chars</div>
            </div>

            <div>
              <label htmlFor="sign-notes" className="block text-xs font-medium text-slate-700 mb-1">Notes (optional)</label>
              <textarea id="sign-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm min-h-[60px]"
                        data-testid="phase6-sign-notes" />
            </div>
          </div>

          <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2 bg-slate-50">
            <button type="button" onClick={onClose} disabled={signing}
                    className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-100">Cancel</button>
            <button type="submit" disabled={signing}
                    className="px-4 py-1.5 text-sm rounded bg-emerald-700 hover:bg-emerald-800 text-white font-medium flex items-center gap-1"
                    data-testid="phase6-sign-submit">
              {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
              Sign as {user?.email}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
